import isEqual from 'lodash-es/isEqual'

import type { useTiers } from '../../../../../Perks/hooks/useTiers'
import type { useWelcomePostsQuery } from '../../hooks/useWelcomePostsQuery'
import type { FormValues } from '../../types'
import { Usage } from '../../types'

export const getWelcomePostFormData = (
  welcomePostsQuery: ReturnType<typeof useWelcomePostsQuery>['welcomePostsQuery'],
): FormValues['welcomePost'] => {
  if (welcomePostsQuery.data?.type === 'Single') {
    const welcomePost = welcomePostsQuery.data.posts[0]

    return {
      postType: welcomePost.type,
      title: welcomePost.title,
      description: welcomePost.description,
      image:
        welcomePost.type === 'Image' &&
        'image' in welcomePost &&
        welcomePost.image &&
        'url' in welcomePost.image
          ? welcomePost.image.url
          : undefined,
      imageResourceId:
        welcomePost.type === 'Image' &&
        'imageResources' in welcomePost &&
        welcomePost.imageResources &&
        welcomePost.imageResources[0]
          ? welcomePost.imageResources[0].resourceId
          : undefined,
      video: 'mediaUrl' in welcomePost ? welcomePost.mediaUrl : undefined,
      videoThumbnail:
        welcomePost.type == 'Video' &&
        'image' in welcomePost &&
        welcomePost.image &&
        'url' in welcomePost.image
          ? welcomePost.image.url
          : undefined,
      videoType: 'upload',
      imageType: 'image' in welcomePost && welcomePost.image ? welcomePost.image.type : undefined,
      status: welcomePost.statuses.resources,
    }
  }

  return {
    postType: 'Text',
    title: '',
    videoType: 'upload',
  }
}

export const getWelcomePostsFormData = (
  welcomePostsQuery: ReturnType<typeof useWelcomePostsQuery>['welcomePostsQuery'],
  tiers: NonNullable<ReturnType<typeof useTiers>['tiers']>,
): FormValues['welcomePosts'] => {
  if (welcomePostsQuery.data?.type === 'Different') {
    const posts = welcomePostsQuery.data.posts

    return tiers.reduce(
      (welcomePosts, tier) => {
        const welcomePost = posts.find((post) => isEqual(post.access.tiers?.[0], { id: tier.id }))

        if (welcomePost) {
          welcomePosts[tier.id] = {
            postType: welcomePost.type,
            title: welcomePost.title,
            description: welcomePost.description,
            image:
              'image' in welcomePost && welcomePost.image && 'url' in welcomePost.image
                ? welcomePost.image.url
                : undefined,
            video: 'mediaUrl' in welcomePost ? welcomePost.mediaUrl : undefined,
            videoThumbnail:
              welcomePost.type == 'Video' &&
              'image' in welcomePost &&
              welcomePost.image &&
              'url' in welcomePost.image
                ? welcomePost.image.url
                : undefined,
            videoType: 'upload',
            imageType:
              'image' in welcomePost && welcomePost.image ? welcomePost.image.type : undefined,
            status: welcomePost.statuses.resources,
          }
        } else {
          welcomePosts[tier.id] = {
            postType: 'Text',
            title: '',
            videoType: 'upload',
          }
        }

        return welcomePosts
      },
      {} as FormValues['welcomePosts'],
    )
  }

  return tiers.reduce(
    (tiers, tier) => {
      tiers[tier.id] = {
        postType: 'Text',
        title: '',
        videoType: 'upload',
      }

      return tiers
    },
    {} as FormValues['welcomePosts'],
  )
}

export const getUsage = (
  welcomePostsQuery: ReturnType<typeof useWelcomePostsQuery>['welcomePostsQuery'],
): Usage => {
  switch (welcomePostsQuery.data?.type) {
    case 'Single':
      return Usage.same
    case 'Different':
      return Usage.different
    case 'Archived':
    default:
      return Usage.none
  }
}
